import { Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { ActivityReportType } from 'containers/activity-reports/ActivityReportFilter/ActivityReportFilter';
import { decryptUnassignedProjectId } from 'containers/activity-reports/hooks/ActivitySummaryQueryUtils';
import useActivityReportDetailColumn, {
  activityReportDetailId,
  isActivityRowRestricted,
  timeRangeFromActivityDateItem,
} from 'containers/activity-reports/hooks/useActivityReportDetailColumn';
import useActivityReportExport from 'containers/activity-reports/hooks/useActivityReportExport';
import { ChannelIds } from 'contexts/ChannelContext/ChannelIds';
import { useChannelListener } from 'contexts/ChannelContext/hooks/useChannelListener';
import { useToastOpen } from 'contexts/ToastContext';
import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import ITimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import { remainingDataItemId } from 'utils/constants/utilConstants';
import { t } from 'utils/localize';
import { ProjectActivityTable } from '../ProjectActivityTable/ProjectActivityTable';
import { IProjectActivityTableRowInfo } from '../hooks/useProjectActivity';
import useProjectActivityDetails from '../hooks/useProjectActivityDetails';

export interface IProjectActivityReportDetailsProps {
  className?: string | object;
  filterId: string;
  filterType: ActivityReportType;
  secondaryReportType: ActivityReportType;
  dateRangeReportType?: TimeRangeType;
  timeRange: ITimeRange<DateTime>;
  onRowSelected: (id: string, secondaryId?: string, isUnassignedProjectId?: boolean) => void;
  onDateRowSelected: (id: string, startDate: DateTime, endDate: DateTime, isUnassignedProjectId?: boolean) => void;
  setExportFunction: (exportFunction: () => object[]) => void;
  scroller: HTMLElement | null;
}

const ProjectActivityReportDetailsTable = (props: IProjectActivityReportDetailsProps) => {
  const {
    className,
    filterId,
    filterType,
    secondaryReportType,
    dateRangeReportType,
    timeRange,
    onRowSelected,
    onDateRowSelected,
    setExportFunction,
    scroller,
  } = props;
  const classes = classNames('project-activity-report-details', className);

  const openToast = useToastOpen();
  const loadingData = useProjectActivityDetails(
    filterId,
    filterType,
    secondaryReportType,
    timeRange,
    dateRangeReportType
  );
  const { exportDetailColumn, getExportData } = useActivityReportDetailColumn(secondaryReportType, dateRangeReportType);
  const getActivityExportData = useActivityReportExport();

  useEffect(() => {
    setExportFunction(() => {
      const data = loadingData.remainingData
        ? loadingData.sortedData.concat(loadingData.remainingData)
        : loadingData.sortedData;

      const exportData = getExportData(data);

      return getActivityExportData(exportData, (row) => {
        return {
          [t('Project')]: row.project?.title ?? t('No Project'),
          ...exportDetailColumn(row),
        };
      });
    });
  }, [loadingData.sortedData]);

  const [lazyLoadingEnabled, setLazyLoadingEnabled] = useState(true);
  useChannelListener(ChannelIds.LazyLoadUpdate, () => setLazyLoadingEnabled(false));

  function rowClickAction(rowItem: IProjectActivityTableRowInfo) {
    if (isActivityRowRestricted(secondaryReportType, rowItem)) {
      openToast({ label: t('Access restricted'), timeout: 3000, theme: Theme.DANGER });
      return;
    }

    const idResult = decryptUnassignedProjectId(rowItem.id);
    const projectId = isNil(rowItem.project) ? remainingDataItemId : rowItem.project.id;

    if (secondaryReportType === ActivityReportType.BY_DATE_RANGE || secondaryReportType === ActivityReportType.BY_DAY) {
      const selectedTimeRange = timeRangeFromActivityDateItem(rowItem);

      if (!isNil(selectedTimeRange)) {
        onDateRowSelected(projectId, selectedTimeRange.startTime, selectedTimeRange.endTime, idResult.unassigned);
        return;
      }
    }

    const secondaryId = activityReportDetailId(secondaryReportType, rowItem);
    onRowSelected(projectId, secondaryId, idResult.unassigned);
  }

  return (
    <>
      <ProjectActivityTable
        className={classes}
        loadingData={loadingData}
        secondaryReportType={secondaryReportType}
        dateRangeReportType={dateRangeReportType}
        scroller={scroller}
        onRowSelected={rowClickAction}
        showTotalFooter={true}
        lazyLoadingEnabled={lazyLoadingEnabled}
      />
    </>
  );
};

export default ProjectActivityReportDetailsTable;
